'use client';

/**
 * MFA Enrollment Wizard
 * A blocking modal that forces users to set up 2FA before accessing admin pages.
 * Cannot be dismissed — the user must complete enrolment.
 */
import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth/AuthContext';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, Copy, Check, Loader2, ShieldCheck } from 'lucide-react';
type WizardStep = 'enrol-totp' | 'success';
interface MFAEnrollmentWizardProps {
  isOpen: boolean;
  onComplete: () => void;
}
export function MFAEnrollmentWizard({
  isOpen,
  onComplete
}: MFAEnrollmentWizardProps) {
  const {
    enrollMFA,
    verifyMFA,
    getMFAFactors,
    unenrollMFA
  } = useAuth();
  const [step, setStep] = useState<WizardStep>('enrol-totp');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // TOTP state
  const [totpData, setTotpData] = useState<{
    id: string;
    qr_code: string;
    secret: string;
  } | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setTotpData(null);
      setError('');
      startTotpEnrolment();
    }
  }, [isOpen]);
  const startTotpEnrolment = async () => {
    setLoading(true);
    setError('');

    // Clear orphaned, unverified TOTP factors before enrolling. Supabase
    // enforces friendly-name uniqueness across ALL factors (verified or not),
    // so a stale 'Authenticator App' left by an abandoned enrolment makes a
    // fresh enroll fail with mfa_factor_name_conflict — trapping the user in
    // this non-dismissable modal (e.g. when re-entering the shell after a
    // password reset). See AuthContext.getMFAFactors for the verified-only
    // caveat that hides these orphans from the rest of the UI.
    const {
      factors
    } = await getMFAFactors();
    if (factors.some(f => f.status === 'verified')) {
      // Already protected — the enforcement gate raced us. Nothing to enrol.
      onComplete();
      return;
    }
    for (const factor of factors.filter(f_0 => f_0.status === 'unverified' && f_0.factor_type === 'totp')) {
      await unenrollMFA(factor.id);
    }
    const {
      data,
      error: error_0
    } = await enrollMFA('totp');
    if (error_0) {
      setError(error_0.message);
      setLoading(false);
      return;
    }
    if (data && data.type === 'totp') {
      setTotpData({
        id: data.id,
        qr_code: data.totp.qr_code,
        secret: data.totp.secret
      });
    }
    setLoading(false);
  };
  const handleVerifyTotp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!totpData) return;
    setError('');
    setLoading(true);
    const {
      error: error_1
    } = await verifyMFA(totpData.id, totpCode);
    if (error_1) {
      setError(error_1.message || 'Invalid verification code');
      setTotpCode('');
    } else {
      setStep('success');
      setTimeout(onComplete, 2000);
    }
    setLoading(false);
  };
  const copySecret = () => {
    if (totpData?.secret) {
      navigator.clipboard.writeText(totpData.secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    }
  };
  const stepTitle: Record<WizardStep, string> = {
    'enrol-totp': 'Set Up Two-Factor Authentication',
    'success': 'Two-Factor Authentication Enabled'
  };

  // noop — modal cannot be closed
  const noop = () => {};
  return <Modal isOpen={isOpen} onClose={noop} title={stepTitle[step]} description="Your organisation requires two-factor authentication. Please set it up to continue." showCloseButton={false} size="lg">
      <div className="space-y-4">
        {error && <div className="p-3 rounded-md bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>}

        {/* TOTP setup loading state */}
        {step === 'enrol-totp' && !totpData && loading && <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Setting up authenticator...
          </div>}

        {/* TOTP Enrolment */}
        {step === 'enrol-totp' && totpData && <div className="space-y-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-4">
                Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
              </p>
              <div className="inline-block p-4 bg-white rounded-lg shadow-sm">
                <img src={totpData.qr_code} alt="QR Code for 2FA" className="w-48 h-48" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Or enter this code manually:</Label>
              <div className="flex gap-2">
                <code className="flex-1 p-3 bg-muted rounded-md font-mono text-sm break-all">
                  {totpData.secret}
                </code>
                <Button variant="outline" size="icon" onClick={copySecret}>
                  {secretCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <form onSubmit={handleVerifyTotp} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="totp-code">Enter verification code</Label>
                <Input id="totp-code" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} placeholder="000000" value={totpCode} onChange={e_0 => setTotpCode(e_0.target.value.replace(/\D/g, ''))} className="text-center text-2xl tracking-widest font-mono" autoFocus />
              </div>
              <Button type="submit" disabled={loading || totpCode.length !== 6} className="w-full">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Shield className="h-4 w-4 mr-2" />}
                Verify and Enable
              </Button>
            </form>
          </div>}

        {/* Step 3: Success */}
        {step === 'success' && <div className="flex flex-col items-center gap-4 py-6">
            <ShieldCheck className="h-16 w-16 text-green-500" />
            <div className="text-center">
              <p className="text-lg font-medium">Two-factor authentication is now active</p>
              <p className="text-sm text-muted-foreground mt-1">
                Your account is now secured. Redirecting to the dashboard...
              </p>
            </div>
          </div>}
      </div>
    </Modal>;
}